import { useId, type ReactNode } from 'react';

import styles from './Field.module.css';

export interface FieldProps {
  readonly label: string;
  readonly hint?: ReactNode;
  readonly error?: string;
  readonly required?: boolean;
  /** Receives the generated ids so the control stays correctly associated. */
  readonly children: (ids: { id: string; describedBy: string | undefined }) => ReactNode;
}

/**
 * Owns label/hint/error wiring so no control has to re-implement accessible
 * associations. Controls stay dumb; this component supplies the ids.
 */
export function Field({ label, hint, error, required, children }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = error ? errorId : hint ? hintId : undefined;

  return (
    <div className={styles.group}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {required && (
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children({ id, describedBy })}
      {hint && !error && (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
